import * as styles from "./TimeRangePickerTrack.css";

import { clsx } from "clsx";
import { useRef } from "react";
import type { JSX, PointerEvent } from "react";

import { TooltipProvider } from "@frontend/components/atoms/Tooltip";

import { useTimeRangePickerContext } from "./TimeRangePicker.context";
import type { TimeRangePickerTrackProps } from "./TimeRangePicker.types";
import { TimeRangePickerRange } from "./TimeRangePickerRange";
import { clamp, fromMinuteRange, ticksBetween } from "./timeMath";
import { useZoomGestures } from "./useZoomGestures";

type Draft = {
  pointerId: number;
  anchor: number;
  index: number | null;
};

export function TimeRangePickerTrack({
  className,
  renderLabel,
  tooltip = true,
  removable = true,
  zoom = true,
  children,
  ...aria
}: TimeRangePickerTrackProps): JSX.Element {
  const {
    ranges,
    view,
    step,
    minDuration,
    majorStep,
    minorStep,
    disabled,
    readOnly,
    trackRef,
    select,
    addRange,
    updateRange,
    commit,
    boundsOf,
    minutesAt,
    percentOf,
    zoomAt,
    panBy,
  } = useTimeRangePickerContext();

  const span = view.end - view.begin;

  useZoomGestures(trackRef, {
    enabled: zoom && !disabled,
    span,
    zoomAt,
    panBy,
    anchorAt: (clientX) => {
      const rect = trackRef.current?.getBoundingClientRect();

      if (!rect || 0 === rect.width) {
        return view.begin;
      }

      return view.begin + ((clientX - rect.left) / rect.width) * span;
    },
  });

  const editable = !disabled && !readOnly;
  const draft = useRef<Draft | null>(null);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || 0 !== event.button) {
      return;
    }

    select(null);

    if (!editable) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    draft.current = {
      pointerId: event.pointerId,
      anchor: minutesAt(event.clientX),
      index: null,
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = draft.current;

    if (!current || current.pointerId !== event.pointerId) {
      return;
    }

    const cursor = minutesAt(event.clientX);

    if (null === current.index && Math.abs(cursor - current.anchor) < step) {
      return;
    }

    const bounds = boundsOf(current.index, current.anchor);
    const begin = clamp(
      Math.min(current.anchor, cursor),
      bounds.begin,
      bounds.end - minDuration,
    );
    const end = clamp(
      Math.max(current.anchor, cursor),
      begin + minDuration,
      bounds.end,
    );

    if (null === current.index) {
      current.index = addRange({ begin, end });
      select(current.index);
    } else {
      updateRange(current.index, { begin, end });
    }
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const current = draft.current;

    if (!current || current.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    draft.current = null;

    if (null !== current.index) {
      commit();
    }
  };

  const minorTicks = ticksBetween(view.begin, view.end, minorStep).filter(
    (tick) => tick !== view.begin && tick !== view.end,
  );

  return (
    <div
      ref={trackRef}
      data-slot="time-range-picker-track"
      data-editable={editable || undefined}
      className={clsx(styles.track, className)}
      role="listbox"
      aria-label={aria["aria-label"]}
      aria-labelledby={aria["aria-labelledby"]}
      aria-disabled={disabled || undefined}
      aria-readonly={readOnly || undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {minorTicks.map((tick) => (
        <div
          key={tick}
          className={styles.gridline}
          data-major={0 === tick % majorStep || undefined}
          style={{ left: `${percentOf(tick)}%` }}
        />
      ))}

      <TooltipProvider>
        {ranges.map((range, index) =>
          null === range ? null : (
            <TimeRangePickerRange
              key={index}
              index={index}
              tooltip={tooltip}
              removable={removable}
            >
              {renderLabel?.(fromMinuteRange(range), index)}
            </TimeRangePickerRange>
          ),
        )}
      </TooltipProvider>

      {children}
    </div>
  );
}
